import api from '@/api';
import type { CompetencyFramework } from '@/api/queries/competency-framework/types';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { customQueryClient } from '@/config/query-client';
import normalize from '@/helpers/normalize';
import type { Translate } from '@/plugins/i18n/types';
import { useQuery } from '@tanstack/vue-query';
import { computed, inject } from 'vue';

export const fetchCompetencyFrameworksList = async (companyUuid: string) => {
  const params = {
    per_page: 0,
    order: 'competency.label:asc',
  };

  const res = await api<{ body: CompetencyFramework[] }>({
    method: 'get',
    url: `/company/${companyUuid}/competency-framework`,
    params,
  });

  return res?.body ?? null;
};

export function useFrameworksListQuery() {
  const { auth, permissions } = useMeQuery();
  const t = inject('t') as Translate;

  const companyId = computed(() => auth.value?.employee?.company_uuid);
  const enabled = computed(() => {
    const hasCompanyId = Boolean(companyId.value);
    const hasRequiredPermissions =
      permissions.value?.includes('mng_r') ||
      permissions.value?.includes('cfi_r') ||
      permissions.value?.includes('cfg_r');

    return hasCompanyId && hasRequiredPermissions;
  });

  const {
    data: frameworks,
    suspense: frameworksListSuspense,
    isFetching: frameworkListIsLoading,
  } = useQuery(
    {
      queryKey: ['company', companyId, 'competency-frameworks'],
      queryFn: () => fetchCompetencyFrameworksList(companyId.value as string),

      enabled,
    },
    customQueryClient,
  );

  const frameworksListNormalized = computed(() => {
    if (frameworks.value && frameworks.value.length > 0) {
      return normalize(frameworks.value, 'uuid');
    }

    return undefined;
  });

  const frameworksSelectOptions = computed(() => {
    const uuids = frameworksListNormalized.value
      ? Object.keys(frameworksListNormalized.value)
      : undefined;

    if (uuids) {
      const frameworkList = uuids.reduce<{ value: string; title: string; devPlan: boolean }[]>(
        (acc, current) => {
          if (frameworksListNormalized.value && frameworksListNormalized.value[current]) {
            acc.push({
              value: current,
              title: t(frameworksListNormalized.value[current].label, undefined, true),
              devPlan: frameworksListNormalized.value[current].dev_plan,
            });
          }
          return acc;
        },
        [],
      );

      return frameworkList;
    }

    return [];
  });

  const frameworksWithDevPlanSelect = computed(() => {
    const uuids = frameworksListNormalized.value
      ? Object.keys(frameworksListNormalized.value)
      : undefined;

    if (uuids) {
      const frameworkList = uuids.reduce<{ value: string; title: string }[]>((acc, current) => {
        if (
          frameworksListNormalized.value &&
          frameworksListNormalized.value[current] &&
          frameworksListNormalized.value[current].dev_plan
        ) {
          acc.push({
            value: current,
            title: t(frameworksListNormalized.value[current].label, undefined, true),
          });
        }
        return acc;
      }, []);

      return frameworkList;
    }

    return [];
  });

  const hasSomeDevPlanActive = computed(() => {
    return frameworks.value?.some((framework) => framework.dev_plan);
  });

  const cFAvailable = computed(() => {
    return permissions.value?.includes('cfi_r') && frameworks.value && frameworks.value.length > 0;
  });

  return {
    cFAvailable,
    frameworksListNormalized,
    frameworksSelectOptions,
    frameworksWithDevPlanSelect,
    frameworksListSuspense,
    frameworkListIsLoading,
    hasSomeDevPlanActive,
  };
}
